import React, { useEffect, useState } from "react";
import {
  Card,
  Input,
  Checkbox,
  DatePicker,
  TimePicker,
  Button,
  Row,
  Col,
  Typography,
  Space,
  message,
} from "antd";
import dayjs, { Dayjs } from "dayjs";
import { useLocation, useNavigate } from "react-router-dom";
import { updateAnnouncement } from "src/services/AdminPortalAPIs";

const { Title, Text } = Typography;
const { TextArea } = Input;

const plans = [
  "All Users",
  "Free Plan",
  "Basic Plan",
  "Pro Plan",
  "Enterprise Plan",
  "Inactive Users",
  "New Users (< 30 days)",
];

const EditAnnouncement: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const announcement = location.state?.announcement;

  const [title, setTitle] = useState("");
  const [content, setContent] = useState("");
  const [targetAudience, setTargetAudience] = useState<string[]>([]);
  const [updating] = useState(false);
  const [publishImmediately, setPublishImmediately] = useState(false);
  const [scheduleDate, setScheduleDate] = useState<Dayjs | null>(null);
  const [actionUrl, setActionUrl] = useState("");
  const [scheduleTime, setScheduleTime] = useState<Dayjs | null>(dayjs());

  useEffect(() => {
    if (!announcement) {
      message.error("No announcement data found.");
      navigate("/admin-portal/announcements");
      return;
    }

    setTitle(announcement.title || "");
    setContent(announcement.content || "");
    setActionUrl(announcement.url || "");

    // Normalize case for comparison
    const normalizedAudience = announcement.target_audience?.toLowerCase();
    const matchedPlan = plans.find(
      plan => plan.toLowerCase() === normalizedAudience
    );

    setTargetAudience(matchedPlan ? [matchedPlan] : []);

    setPublishImmediately(announcement.status === "published");

    if (announcement.scheduled_for) {
      const schedule = dayjs(announcement.scheduled_for);
      setScheduleDate(schedule);
      setScheduleTime(schedule);
    }
  }, [announcement, navigate]);

  const handleAudienceChange = (checkedValues: any[]) => {
    if (checkedValues.length === 0) {
      message.warning("At least one target audience must be selected.");
    } else {
      setTargetAudience(checkedValues);
    }
  };

  const handleSubmit = async () => {
    if (!title.trim()) {
      return message.error("Title is required.");
    }
    if (!content.trim()) {
      return message.error("Content is required.");
    }
    if (targetAudience.length === 0) {
      return message.error("At least one audience must be selected.");
    }

    const status: "scheduled" | "published" = publishImmediately
      ? "published"
      : "scheduled";

    let scheduled_for: string | null = null;

    if (status === "scheduled") {
      if (!scheduleDate || !scheduleTime) {
        return message.error("Schedule date and time are required.");
      }
      scheduled_for = `${scheduleDate.format(
        "YYYY-MM-DD"
      )}T${scheduleTime.format("HH:mm:ss")}.000Z`;
    }

    if (actionUrl && !/^https?:\/\/.+\..+/.test(actionUrl.trim())) {
      return message.error("Please enter a valid URL.");
    }

    const payload = {
      announcement_id: announcement.announcement_id,
      title: title.trim(),
      content: content.trim(),
      target_audience:
        targetAudience[0] === "New Users (< 30 days)"
          ? "new users"
          : targetAudience[0],
      scheduled_for,
      status,
      ...(actionUrl.trim() && { url: actionUrl.trim() }),
    };

    try {
      const res = await updateAnnouncement(payload);
      if (res.data?.message?.includes("No changes")) {
        message.info("No changes were made.");
      } else {
        message.success("Announcement updated successfully.");
      }
      navigate("/admin-portal/announcements");
    } catch (error) {
      message.error("Failed to update announcement.");
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <button
        className="button button--primary"
        onClick={() => navigate("/admin-portal/announcements")}
      >
        <span>&lt;&nbsp;</span> Go Back
      </button>

      <Title level={2}>Edit Announcement</Title>

      <Row gutter={24}>
        <Col span={14}>
          <Card title="Announcement Content">
            <Space direction="vertical" size="large" style={{ width: "100%" }}>
              <div>
                <Text strong>Title</Text>
                <Input
                  placeholder="Enter announcement title"
                  value={title}
                  aria-label="Announcement Title"
                  onChange={e => setTitle(e.target.value)}
                />
              </div>
              <div>
                <Text strong>Content</Text>
                <TextArea
                  rows={6}
                  placeholder="Enter announcement content..."
                  value={content}
                  onChange={e => setContent(e.target.value)}
                />
              </div>
              <div>
                <Text strong>
                  Action Button URL <Text type="secondary">(optional)</Text>
                </Text>
                <Input
                  placeholder="https://example.com/announcement-action"
                  value={actionUrl}
                  onChange={e => setActionUrl(e.target.value)}
                  type="url"
                />
              </div>
            </Space>
          </Card>
        </Col>

        <Col span={10}>
          <Card title="Publishing Settings">
            <Text strong>Target Audience</Text>
            <div style={{ marginTop: 12, marginBottom: 16 }}>
              <Checkbox.Group
                value={targetAudience}
                onChange={handleAudienceChange}
              >
                <Space direction="vertical">
                  {plans.map(plan => (
                    <Checkbox key={plan} value={plan}>
                      {plan}
                    </Checkbox>
                  ))}
                </Space>
              </Checkbox.Group>
            </div>

            <Checkbox
              checked={publishImmediately}
              onChange={e => setPublishImmediately(e.target.checked)}
              style={{ marginBottom: 16 }}
            >
              Publish immediately
            </Checkbox>

            {!publishImmediately && (
              <Space direction="vertical" style={{ width: "100%" }}>
                <div>
                  <Text strong>Schedule Date</Text>
                  <DatePicker
                    style={{ width: "100%" }}
                    value={scheduleDate}
                    onChange={setScheduleDate}
                  />
                </div>
                <div>
                  <Text strong>Schedule Time</Text>
                  <TimePicker
                    use12Hours
                    format="h:mm A"
                    style={{ width: "100%" }}
                    value={scheduleTime}
                    onChange={setScheduleTime}
                  />
                </div>
              </Space>
            )}
          </Card>
        </Col>
      </Row>

      <div style={{ marginTop: 24, textAlign: "right" }}>
        <Button
          style={{ marginRight: 12 }}
          aria-label="Cancel"
          onClick={() => navigate("/admin-portal/announcements")}
        >
          Cancel
        </Button>
        <Button
          type="primary"
          aria-label="Update Announcement"
          onClick={handleSubmit}
          loading={updating}
          disabled={!publishImmediately && (!scheduleDate || !scheduleTime)}
        >
          Update
        </Button>
      </div>
    </div>
  );
};

export default EditAnnouncement;
